(() => {
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const one = (selector, root = document) => root.querySelector(selector);
  const all = (selector, root = document) => [...root.querySelectorAll(selector)];
  const scrollSlideHorizontally = (track, slide, inline = 'start', behavior = reducedMotion ? 'auto' : 'smooth') => {
    if (!track || !slide) return;
    const trackRect = track.getBoundingClientRect();
    const slideRect = slide.getBoundingClientRect();
    let left;

    if (inline === 'center') {
      left = slideRect.left + (slideRect.width / 2) - trackRect.left - (trackRect.width / 2);
    } else if (getComputedStyle(track).direction === 'rtl') {
      left = slideRect.right - trackRect.right;
    } else {
      left = slideRect.left - trackRect.left;
    }

    track.scrollBy({ left, behavior });
  };

  class PdpCarousel extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.track = one('[data-track]', this);
      this.slides = all('[data-slide]', this);
      this.dots = all('[data-dot]', this);
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      one('[data-prev]', this)?.addEventListener('click', () => this.go(-1), options);
      one('[data-next]', this)?.addEventListener('click', () => this.go(1), options);
      this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.to(index), options));
      this.track?.addEventListener('scroll', () => requestAnimationFrame(() => this.sync()), { ...options, passive: true });
      ['pointerenter', 'focusin', 'pointerdown'].forEach((eventName) => this.addEventListener(eventName, () => this.pause(), options));
      if (this.dataset.autoplay === 'true' && !reducedMotion) this.play();
    }
    disconnectedCallback() { this.abort?.abort(); this.pause(); }
    index() {
      if (!this.track || !this.slides.length) return 0;
      const centered = getComputedStyle(this.slides[0]).scrollSnapAlign.includes('center');
      const position = (slide) => centered
        ? Math.abs((slide.offsetLeft + (slide.offsetWidth / 2)) - (this.track.scrollLeft + (this.track.clientWidth / 2)))
        : Math.abs(slide.offsetLeft - this.track.scrollLeft);
      return this.slides.reduce((closest, slide, index) => position(slide) < position(this.slides[closest]) ? index : closest, 0);
    }
    to(index) {
      const slide = this.slides[(index + this.slides.length) % this.slides.length];
      const inline = getComputedStyle(slide).scrollSnapAlign.includes('center') ? 'center' : 'start';
      scrollSlideHorizontally(this.track, slide, inline);
    }
    go(direction) { this.to(this.index() + direction); }
    sync() {
      const index = this.index();
      this.dots.forEach((dot, dotIndex) => dot.classList.toggle('is-active', dotIndex === index));
      this.dots.forEach((dot, dotIndex) => dot.setAttribute('aria-current', dotIndex === index ? 'true' : 'false'));
    }
    play() { this.pause(); this.timer = setInterval(() => this.go(1), (Number(this.dataset.speed) || 5) * 1000); }
    pause() { clearInterval(this.timer); }
  }

  class PdpAccordion extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.abort = new AbortController();
      if (this.dataset.single === 'true') {
        all('details', this).forEach((details) => details.addEventListener('toggle', () => {
          if (details.open) all('details', this).forEach((item) => { if (item !== details) item.open = false; });
        }, { signal: this.abort.signal }));
      }
    }
    disconnectedCallback() { this.abort?.abort(); }
  }

  class PdpMain extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.media = one('[data-media-track]', this);
      all('[data-thumb]', this).forEach((button, index) => button.addEventListener('click', () => this.showMedia(index), options));
      one('[data-media-prev]', this)?.addEventListener('click', () => this.showMedia(this.mediaIndex() - 1), options);
      one('[data-media-next]', this)?.addEventListener('click', () => this.showMedia(this.mediaIndex() + 1), options);
      this.media?.addEventListener('scroll', () => requestAnimationFrame(() => this.syncThumb()), { ...options, passive: true });
      one('[data-gallery-open]', this)?.addEventListener('click', () => one('[data-gallery-dialog]', this)?.showModal(), options);
      one('[data-gallery-close]', this)?.addEventListener('click', () => one('[data-gallery-dialog]', this)?.close(), options);
      one('[data-gallery-dialog]', this)?.addEventListener('click', (event) => { if (event.target === event.currentTarget) event.currentTarget.close(); }, options);
      one('[data-review-link]', this)?.addEventListener('click', (event) => { event.preventDefault(); one('#pdp-reviews')?.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth' }); }, options);
      all('[data-pdp-option]', this).forEach((group) => group.addEventListener('change', () => this.variant(), options));
      try { this.variants = JSON.parse(one('[data-variants]', this)?.textContent || '[]'); } catch { this.variants = []; }
      this.updateAvailability();
    }
    disconnectedCallback() { this.abort?.abort(); }
    mediaIndex() {
      const slides = all('[data-media]', this);
      if (!slides.length || !this.media) return 0;
      return slides.reduce((closest, slide, index) => Math.abs(slide.offsetLeft - this.media.scrollLeft) < Math.abs(slides[closest].offsetLeft - this.media.scrollLeft) ? index : closest, 0);
    }
    showMedia(index) {
      const slides = all('[data-media]', this);
      if (!slides.length) return;
      scrollSlideHorizontally(this.media, slides[(index + slides.length) % slides.length]);
    }
    syncThumb() {
      const slides = all('[data-media]', this);
      const index = this.mediaIndex();
      all('[data-thumb]', this).forEach((button, buttonIndex) => button.setAttribute('aria-current', buttonIndex === index ? 'true' : 'false'));
      slides.forEach((slide, slideIndex) => {
        if (slideIndex !== index) {
          all('video', slide).forEach((video) => video.pause());
          all('iframe', slide).forEach((frame) => frame.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo' }), '*'));
        }
      });
    }
    values() { return all('[data-pdp-option]', this).map((group) => one('input:checked, select', group)?.value); }
    updateAvailability() {
      const chosen = this.values();
      all('[data-pdp-option]', this).forEach((group, optionIndex) => all('input, option', group).forEach((control) => {
        control.disabled = !this.variants.some((variant) => variant.available && variant.options[optionIndex] === control.value && variant.options.slice(0, optionIndex).every((option, index) => option === chosen[index]));
      }));
    }
    variant() {
      const values = this.values();
      const variant = this.variants.find((item) => item.options.every((option, index) => option === values[index]));
      const button = one('[data-main-submit]', this);
      if (!variant) {
        if (button) { button.disabled = true; one('[data-atc-text]', button).textContent = this.dataset.unavailable || this.dataset.sold; }
        return;
      }
      this.updateAvailability();
      all('input[name="id"]', this).forEach((input) => { input.value = variant.id; });
      if (button) {
        button.disabled = !variant.available;
        button.dataset.unavailable = variant.available ? 'false' : 'true';
        one('[data-atc-text]', button).textContent = variant.available ? this.dataset.add : this.dataset.sold;
      }
      const price = one('[data-price]', this);
      if (price) price.innerHTML = `${variant.compare_at_price > variant.price ? `<s>${this.money(variant.compare_at_price)}</s> ` : ''}<span>${this.money(variant.price)}</span>`;
      if (variant.featured_media) {
        const slide = one(`[data-media-id="${variant.featured_media.id}"]`, this);
        if (slide) this.showMedia(all('[data-media]', this).indexOf(slide));
      }
      history.replaceState({}, '', `${this.dataset.url}?variant=${variant.id}`);
      document.dispatchEvent(new CustomEvent('variant:change', { detail: { variant, sectionId: this.dataset.section } }));
    }
    money(cents) {
      const format = this.dataset.money || '${{amount}}';
      const noDecimals = /amount_no_decimals/.test(format);
      const amount = (cents / 100).toLocaleString(undefined, { minimumFractionDigits: noDecimals ? 0 : 2, maximumFractionDigits: noDecimals ? 0 : 2 });
      return format.replace(/\{\{\s*amount[^}]*\}\}/, amount);
    }
  }

  class PdpCountdown extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      const duration = Math.max(60, Number(this.dataset.duration) || 14400) * 1000;
      const storageKey = this.dataset.storageKey;
      let storedEnd = 0;
      try { storedEnd = Number(sessionStorage.getItem(storageKey)); } catch { /* Storage can be unavailable in strict privacy modes. */ }
      this.end = storedEnd > Date.now() ? storedEnd : Date.now() + duration;
      try { sessionStorage.setItem(storageKey, String(this.end)); } catch { /* The countdown still works without persistence. */ }
      this.render();
      this.interval = setInterval(() => this.render(), 1000);
    }
    disconnectedCallback() { clearInterval(this.interval); }
    render() {
      let remaining = Math.max(0, this.end - Date.now());
      if (!remaining) {
        const duration = Math.max(60, Number(this.dataset.duration) || 14400) * 1000;
        this.end = Date.now() + duration;
        try { sessionStorage.setItem(this.dataset.storageKey, String(this.end)); } catch { /* Keep the in-memory timer running. */ }
        remaining = duration;
      }
      const totalSeconds = Math.floor(remaining / 1000);
      const values = {
        hours: Math.floor(totalSeconds / 3600),
        minutes: Math.floor((totalSeconds % 3600) / 60),
        seconds: totalSeconds % 60
      };
      Object.entries(values).forEach(([unit, value]) => {
        const target = one(`[data-countdown-${unit}]`, this);
        if (target) target.textContent = String(value).padStart(2, '0');
      });
    }
  }

  class PdpSticky extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.main = one(this.dataset.target);
      this.footer = one('[data-pdp-footer]');
      this.button = one('button', this);
      if (!this.main || !this.button) return;
      this.abort = new AbortController();
      this.button.addEventListener('click', () => this.main.click(), { signal: this.abort.signal });
      this.mutation = new MutationObserver(() => this.sync());
      this.mutation.observe(this.main, { attributes: true, childList: true, subtree: true, characterData: true });
      this.mainObserver = new IntersectionObserver((entries) => { this.mainVisible = entries[0].isIntersecting; this.show(); }, { threshold: Number(this.dataset.threshold) || .1 });
      this.mainObserver.observe(this.main);
      if (this.footer) {
        this.footerObserver = new IntersectionObserver((entries) => { this.footerVisible = entries[0].isIntersecting; this.show(); });
        this.footerObserver.observe(this.footer);
      }
      this.sync();
    }
    disconnectedCallback() { this.abort?.abort(); this.mutation?.disconnect(); this.mainObserver?.disconnect(); this.footerObserver?.disconnect(); }
    sync() {
      this.button.disabled = this.main.disabled;
      this.button.textContent = one('[data-atc-text]', this.main)?.textContent?.trim() || this.dataset.label;
      this.button.setAttribute('aria-busy', this.main.getAttribute('aria-busy') || 'false');
    }
    show() {
      const visible = !this.mainVisible && !this.footerVisible;
      this.classList.toggle('is-visible', visible);
      document.body.classList.toggle('pdp-sticky-visible', visible);
    }
  }

  class PdpHeader extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      one('[data-menu]', this)?.addEventListener('click', () => one('.shopify-section-group-header-group .header__icon--menu')?.click(), options);
      one('[data-cart]', this)?.addEventListener('click', (event) => { const cart = one('#cart-icon-bubble'); if (cart) { event.preventDefault(); cart.click(); } }, options);
    }
    disconnectedCallback() { this.abort?.abort(); }
  }

  class PdpHeaderShell extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.abort = new AbortController();
      this.section = this.closest('.pdp-header-section');
      this.resizeObserver = new ResizeObserver(() => this.syncOffset());
      this.resizeObserver.observe(this);
      this.announcementSections = all('.shopify-section-group-header-group').filter((section) =>
        !section.classList.contains('section-header') &&
        one('.horizontal-ticker, .announcement-bar', section) &&
        getComputedStyle(section).display !== 'none'
      );
      this.announcementSections.forEach((section) => this.resizeObserver.observe(section));
      this.lastScrollY = Math.max(0, window.scrollY);
      window.addEventListener('scroll', () => this.queueScrollUpdate(), { passive: true, signal: this.abort.signal });
      this.syncOffset();
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.resizeObserver?.disconnect();
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
    }
    syncOffset() {
      let top = 0;
      this.announcementSections?.forEach((announcement) => {
        if (getComputedStyle(announcement).display === 'none') return;
        announcement.style.top = `${top}px`;
        const height = announcement.getBoundingClientRect().height;
        top += height;
        announcement.style.setProperty('--pdp-announcement-hide-distance', `${top}px`);
      });
      this.announcementHeight = top;
      if (this.section) this.section.style.height = `${top + this.getBoundingClientRect().height}px`;
      this.updatePositions(Math.max(0, window.scrollY) <= 1);
    }
    queueScrollUpdate() {
      if (this.scrollFrame) return;
      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = 0;
        const currentScrollY = Math.max(0, window.scrollY);
        const atTop = currentScrollY <= 1;

        if (atTop || currentScrollY < this.lastScrollY) {
          this.classList.remove('is-scroll-hidden');
        } else if (currentScrollY > this.lastScrollY) {
          this.classList.add('is-scroll-hidden');
        }

        this.updatePositions(atTop);
        this.lastScrollY = currentScrollY;
      });
    }
    updatePositions(atTop) {
      this.announcementSections?.forEach((announcement) => {
        announcement.classList.toggle('pdp-global-announcement--hidden', !atTop);
      });
      this.style.top = atTop ? `${this.announcementHeight || 0}px` : '0px';
    }
  }

  class PdpAnnouncement extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.slides = all('[data-announcement-slide]', this);
      this.index = Math.max(0, this.slides.findIndex((slide) => !slide.hidden));
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      const previous = one('[data-announcement-prev]', this);
      const next = one('[data-announcement-next]', this);
      previous?.addEventListener('click', () => this.go(-1), options);
      next?.addEventListener('click', () => this.go(1), options);
      [previous, next].forEach((button) => { if (button) button.disabled = this.slides.length < 2; });
      ['pointerenter', 'focusin'].forEach((eventName) => this.addEventListener(eventName, () => this.pause(), options));
      ['pointerleave', 'focusout'].forEach((eventName) => this.addEventListener(eventName, () => this.play(), options));
      if (this.dataset.autoplay === 'true' && !reducedMotion) this.play();
    }
    disconnectedCallback() { this.abort?.abort(); this.pause(); }
    go(direction) {
      if (this.slides.length < 2) return;
      this.show((this.index + direction + this.slides.length) % this.slides.length);
    }
    show(index) {
      this.slides[this.index].hidden = true;
      this.index = index;
      this.slides[this.index].hidden = false;
    }
    play() {
      this.pause();
      if (this.dataset.autoplay === 'true' && !reducedMotion && this.slides.length > 1) {
        this.timer = setInterval(() => this.go(1), (Number(this.dataset.speed) || 5) * 1000);
      }
    }
    pause() { clearInterval(this.timer); }
  }

  class PdpFeatureSlider extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.slides = all('[data-feature-slide]', this);
      this.dots = all('[data-feature-dot]', this);
      this.current = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.dots.forEach((dot, index) => dot.addEventListener('click', () => {
        this.show(index);
        this.play();
      }, options));
      ['pointerenter', 'focusin'].forEach((eventName) => this.addEventListener(eventName, () => this.pause(), options));
      ['pointerleave', 'focusout'].forEach((eventName) => this.addEventListener(eventName, () => this.play(), options));
      document.addEventListener('visibilitychange', () => document.hidden ? this.pause() : this.play(), options);
      this.sync();
      this.play();
    }
    disconnectedCallback() { this.abort?.abort(); this.pause(); }
    show(index) {
      if (this.slides.length < 2 || index === this.current) return;
      const previous = this.slides[this.current];
      const next = this.slides[(index + this.slides.length) % this.slides.length];
      previous.classList.remove('is-active');
      previous.classList.add('is-leaving');
      previous.setAttribute('aria-hidden', 'true');
      next.classList.remove('is-leaving');
      next.classList.add('is-active');
      next.setAttribute('aria-hidden', 'false');
      this.current = this.slides.indexOf(next);
      this.sync();
      clearTimeout(this.cleanupTimer);
      this.cleanupTimer = setTimeout(() => previous.classList.remove('is-leaving'), 450);
    }
    sync() {
      this.dots.forEach((dot, index) => {
        const active = index === this.current;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });
    }
    play() {
      this.pause();
      if (this.dataset.autoplay === 'true' && !reducedMotion && !document.hidden && this.slides.length > 1) {
        this.timer = setInterval(() => this.show(this.current + 1), (Number(this.dataset.speed) || 2) * 1000);
      }
    }
    pause() { clearInterval(this.timer); }
  }

  if (!customElements.get('pdp-carousel')) customElements.define('pdp-carousel', PdpCarousel);
  if (!customElements.get('pdp-accordion')) customElements.define('pdp-accordion', PdpAccordion);
  if (!customElements.get('pdp-main')) customElements.define('pdp-main', PdpMain);
  if (!customElements.get('pdp-countdown')) customElements.define('pdp-countdown', PdpCountdown);
  if (!customElements.get('pdp-sticky')) customElements.define('pdp-sticky', PdpSticky);
  if (!customElements.get('pdp-header')) customElements.define('pdp-header', PdpHeader);
  if (!customElements.get('pdp-header-shell')) customElements.define('pdp-header-shell', PdpHeaderShell);
  if (!customElements.get('pdp-announcement')) customElements.define('pdp-announcement', PdpAnnouncement);
  if (!customElements.get('pdp-feature-slider')) customElements.define('pdp-feature-slider', PdpFeatureSlider);

  document.addEventListener('shopify:block:select', (event) => {
    const block = event.target;
    block.closest('details')?.setAttribute('open', '');
    const slide = block.closest('[data-slide]');
    if (slide) scrollSlideHorizontally(slide.closest('[data-track]'), slide, 'center', 'auto');
    const announcementSlide = block.closest('[data-announcement-slide]');
    const announcement = announcementSlide?.closest('pdp-announcement');
    if (announcementSlide && announcement) {
      announcement.pause();
      announcement.show(announcement.slides.indexOf(announcementSlide));
    }
  });
})();
